/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type FollowUp } from '../../lib/api/follow-ups.js';
import { useResolveFollowUp } from '../../lib/queries/follow-ups.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

/** Close a follow-up with a mandatory resolution note (CR-011). */
export function ResolveFollowUpModal({
  followUp,
  onClose,
}: {
  readonly followUp: FollowUp | null;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const resolve = useResolveFollowUp();
  const [note, setNote] = useState('');

  const onNote = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  }, []);
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setNote('');
        onClose();
      }
    },
    [onClose],
  );
  const submit = useCallback(() => {
    if (followUp === null || note.trim().length < 2) {
      return;
    }
    resolve.mutate(
      { id: followUp.id, resolutionNote: note.trim() },
      {
        onSuccess: () => {
          setNote('');
          onClose();
        },
      },
    );
  }, [resolve, followUp, note, onClose]);

  return (
    <Modal
      open={followUp !== null}
      onOpenChange={onModal}
      title={t('followUps.resolveTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">{followUp?.title}</p>
        <div>
          <label htmlFor="fu-note" className={LABEL}>
            {t('followUps.resolutionNote')}
          </label>
          <textarea id="fu-note" className={INPUT} rows={3} value={note} onChange={onNote} />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={submit}
            disabled={resolve.isPending || note.trim().length < 2}
          >
            {t('followUps.resolve')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
